import { type ColumnDef, type Column, type SortingState, type RowSelectionState } from "@tanstack/react-table";
import { useSnapshot } from "valtio";
import { useState, useMemo } from "react";
import { DataTable } from "./DataTable";
import { store, actions, type Company } from "../store";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ArrowUp, ArrowDown, Plus, Building2, ChevronDown, Settings, ArrowDownUp, Filter, MessageCircle, HelpCircle, MoreVertical, PanelLeftOpen, Upload } from "lucide-react";
import { AddColumnModal } from "./AddColumnModal";
import { EditableCell } from "./EditableCell";
import { EnrichModal } from "./EnrichModal";
import { Sidebar } from "./Sidebar";
import { AddCompanyModal } from "./AddCompanyModal";
import { TaskBasisPanel } from "./TaskBasisPanel";
import { generateOutputSchema, createExaResearchTask, getExaTaskStatus } from "@/lib/exa-api";
import { createTask, getTaskRun, getTaskRunResult } from "@/lib/parallel-api";
import { enrichWithAnthropic } from "@/lib/anthropic-api";
import { toast } from "sonner";
import { RenameColumnModal } from "./RenameColumnModal";
import { ImportCSVModal } from "./ImportCSVModal";
import { type Workspace } from "@/store";
import { TooltipProvider } from "@/components/ui/tooltip";

const createSortableHeader =
  (title: string, isCustomColumn: boolean = false, onRename?: (columnName: string) => void) =>
  ({ column }: { column: Column<any, unknown> }) =>
    (
      <div className="flex items-center h-full">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-full p-0 hover:bg-transparent text-[#242529] font-medium text-sm">
              {title}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
              <ArrowUp className="mr-2 h-4 w-4" />
              Sort Ascending
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
              <ArrowDown className="mr-2 h-4 w-4" />
              Sort Descending
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => column.clearSorting()}>
              Clear Sort
            </DropdownMenuItem>
            {isCustomColumn && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onRename?.(title)}
                >
                  Rename Column
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => actions.removeColumn(title)}
                  className="text-red-600 focus:text-red-600"
                >
                  Remove Column
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );

export function CompanyTable() {
  const snap = useSnapshot(store);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEnrichModalOpen, setIsEnrichModalOpen] = useState(false);
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: "id", desc: false }]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [basisPanelState, setBasisPanelState] = useState<{
    open: boolean;
    companyId: string;
    columnName: string;
    companyName: string;
  } | null>(null);
  const [renameModalState, setRenameModalState] = useState<{
    open: boolean;
    columnName: string;
  }>({ open: false, columnName: "" });
  const [isImportCSVModalOpen, setIsImportCSVModalOpen] = useState(false);

  const isWorkspacesView = snap.activeView === 'workspaces';
  const currentData = isWorkspacesView ? snap.workspaces : snap.companies;

  // Debug logging
  console.log('Current view:', snap.activeView);
  console.log('Is workspaces view:', isWorkspacesView);
  console.log('Current data length:', currentData.length);
  console.log('Workspaces columns:', snap.workspaceColumns);

  const columns = useMemo(() => {
    console.log('Recalculating columns. isWorkspacesView:', isWorkspacesView);
    if (isWorkspacesView) {
      // Workspaces view - dynamic columns from CSV
      const workspaceColumns: ColumnDef<Workspace>[] = [
        {
          id: "select",
          header: ({ table }) => (
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label="Select row"
            />
          ),
          enableSorting: false,
          enableHiding: false,
          size: 44,
        },
      ];

      // Add dynamic columns from workspace data
      snap.workspaceColumns.forEach((columnName) => {
        workspaceColumns.push({
          accessorKey: columnName,
          header: createSortableHeader(columnName, true, (colName) =>
            setRenameModalState({ open: true, columnName: colName })
          ),
          cell: ({ row }) => (
            <EditableCell
              value={row.getValue(columnName) || ""}
              onSave={(value) =>
                actions.updateWorkspaceField(row.original.id, columnName, value)
              }
              companyId={row.original.id}
              columnName={columnName}
              onShowBasis={() =>
                setBasisPanelState({
                  open: true,
                  companyId: row.original.id,
                  columnName: columnName,
                  companyName: String(row.original[snap.workspaceColumns[0]] || ''),
                })
              }
            />
          ),
          size: 200,
          maxSize: 240,
        });
      });

      // Add column button for workspaces
      const addColumnButton: ColumnDef<Workspace> = {
        id: "add-column",
        header: () => (
          <div className="flex items-center h-full">
            <Button
              variant="ghost"
              onClick={() => setIsModalOpen(true)}
              className="h-full px-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 font-medium text-sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add column
            </Button>
          </div>
        ),
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        size: 140,
      };

      return [...workspaceColumns, addColumnButton];
    }

    // Companies view - original columns
    const baseColumns: ColumnDef<Company>[] = [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 44,
      },
      {
        accessorKey: "id",
        header: createSortableHeader("ID"),
        cell: ({ row }) => (
          <EditableCell
            value={row.getValue("id")}
            onSave={(value) =>
              actions.updateCompanyField(row.original.id, "id", value)
            }
            companyId={row.original.id}
          />
        ),
        size: 80,
        maxSize: 240,
      },
      {
        accessorKey: "name",
        header: ({ column }) => (
          <div className="flex items-center gap-3 h-full">
            <div className="flex-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-full p-0 hover:bg-transparent text-[#242529] font-medium text-sm">
                    Company
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
                    <ArrowUp className="mr-2 h-4 w-4" />
                    Sort Ascending
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
                    <ArrowDown className="mr-2 h-4 w-4" />
                    Sort Descending
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => column.clearSorting()}>
                    Clear Sort
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button
              variant="ghost"
              onClick={() => setIsAddCompanyModalOpen(true)}
              className="w-7 h-7 p-0 rounded-lg flex-shrink-0 hover:bg-gray-100"
            >
              <Plus className="h-3.5 w-3.5 text-gray-600" />
            </Button>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2 h-full px-4">
            <div className="flex-shrink-0 w-4 h-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Building2 className="w-3 h-3 text-gray-500" />
            </div>
            <div className="flex-1 h-full">
              <EditableCell
                value={row.getValue("name")}
                onSave={(value) =>
                  actions.updateCompanyField(row.original.id, "name", value)
                }
                companyId={row.original.id}
                className="font-medium text-[#242529]"
              />
            </div>
          </div>
        ),
        size: 240,
        maxSize: 240,
      },
      {
        accessorKey: "domain",
        header: createSortableHeader("Domains"),
        cell: ({ row }) => {
          const domain = row.getValue("domain") as string;
          return (
            <div className="flex items-center h-full">
              <a
                href={`https://${domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm break-words"
              >
                {domain}
              </a>
            </div>
          );
        },
        size: 220,
        maxSize: 240,
      },
    ];

    const customColumns: ColumnDef<Company>[] = snap.customColumns.map(
      (columnName) => ({
        accessorKey: columnName,
        header: createSortableHeader(columnName, true, (colName) =>
          setRenameModalState({ open: true, columnName: colName })
        ),
        cell: ({ row }) => (
          <EditableCell
            value={row.getValue(columnName) || ""}
            onSave={(value) =>
              actions.updateCompanyField(row.original.id, columnName, value)
            }
            companyId={row.original.id}
            columnName={columnName}
            onShowBasis={() =>
              setBasisPanelState({
                open: true,
                companyId: row.original.id,
                columnName: columnName,
                companyName: row.original.name,
              })
            }
          />
        ),
        size: 200,
        maxSize: 240,
      })
    );

    const addColumnButton: ColumnDef<Company> = {
      id: "add-column",
      header: () => (
        <div className="flex items-center h-full">
          <Button
            variant="ghost"
            onClick={() => setIsModalOpen(true)}
            className="h-full px-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 font-medium text-sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add column
          </Button>
        </div>
      ),
      cell: () => null,
      enableSorting: false,
      enableHiding: false,
      enableResizing: false,
      size: 140,
    };

    return [...baseColumns, ...customColumns, addColumnButton];
  }, [snap.customColumns, snap.workspaceColumns, isWorkspacesView, snap.activeView]);

  const handleAddColumn = async (columnName: string, enrichmentPrompt?: string, provider?: 'exa' | 'parallel' | 'anthropic') => {
    // If enrichment prompt is provided, enrich the column based on provider
    if (enrichmentPrompt && provider) {
      if (provider === 'exa') {
        await handleExaEnrichment(columnName, enrichmentPrompt);
      } else if (provider === 'parallel') {
        await handleParallelEnrichment(columnName, enrichmentPrompt);
      } else if (provider === 'anthropic') {
        await handleAnthropicEnrichment(columnName, enrichmentPrompt);
      }
    } else {
      // No enrichment, just add the column
      actions.addColumn(columnName);
    }
  };

  const handleExaEnrichment = async (columnName: string, exaPrompt: string) => {
      try {
        toast.info(`Generating output schema...`);

        // Step 1: Generate output schema using GPT-4o-mini
        const outputSchema = await generateOutputSchema(exaPrompt);
        console.log('Generated schema:', outputSchema);

        // Add the column with the Exa prompt
        actions.addColumn(columnName, exaPrompt, 'exa');

        // Determine which rows to enrich
        const selectedIndices = Object.keys(rowSelection).filter(key => rowSelection[key]).map(Number);
        const rowsToEnrich = selectedIndices.length > 0
          ? selectedIndices.map(index => currentData[index]).filter(Boolean)
          : currentData;

        console.log(`Enriching ${rowsToEnrich.length} rows (${selectedIndices.length > 0 ? 'selected' : 'all'})`);

        toast.info(`Starting Exa enrichment for ${columnName} on ${rowsToEnrich.length} row${rowsToEnrich.length !== 1 ? 's' : ''}...`);

        // Process each selected/all company
        const enrichPromises = rowsToEnrich.map(async (item) => {
          const displayName = ('name' in item ? String(item.name) : String(item.id));

          try {
            // Mark column as enriching
            actions.setCompanyColumnEnriching(item.id, columnName, true);

            // Replace template variables in prompt
            let query = exaPrompt;

            // Replace all template variables from item data
            Object.keys(item).forEach((key) => {
              if (key !== 'id') {
                const value = item[key];
                const stringValue = Array.isArray(value) ? value.join(', ') : String(value);
                query = query.replace(
                  new RegExp(`\\{${key}\\}`, 'g'),
                  stringValue
                );
              }
            });

            console.log(`Creating Exa research task for ${displayName}:`, query);

            // Step 2: Create Exa research task
            const taskResponse = await createExaResearchTask(query, outputSchema);
            console.log(`Exa task created for ${displayName}:`, taskResponse);

            // Step 3: Poll for task completion
            let taskStatus = await getExaTaskStatus(taskResponse.researchId);
            console.log(`Task status for ${displayName}:`, taskStatus.status);

            while (taskStatus.status === 'pending' || taskStatus.status === 'running' || taskStatus.status === 'processing') {
              await new Promise(resolve => setTimeout(resolve, 2000)); // Poll every 2 seconds
              taskStatus = await getExaTaskStatus(taskResponse.researchId);
              console.log(`Task status for ${displayName}:`, taskStatus.status);
            }

            if (taskStatus.status === 'completed') {
              console.log(`Task completed for ${displayName}. Full response:`, JSON.stringify(taskStatus, null, 2));
              console.log(`Response keys:`, Object.keys(taskStatus));

              if (taskStatus.output?.parsed) {
                console.log(`Using parsed output for ${displayName}:`, taskStatus.output.parsed);

                // Extract value from parsed output
                const outputKeys = Object.keys(outputSchema.properties);
                let valueToStore: string = '';

                if (outputKeys.length === 1) {
                  // Single output field - unwrap and use the value directly
                  const firstKey = outputKeys[0];
                  const value = taskStatus.output.parsed[firstKey];

                  if (value !== undefined) {
                    valueToStore = String(value);
                  }
                } else if (outputKeys.length > 1) {
                  // Multiple output fields - store as JSON string
                  valueToStore = JSON.stringify(taskStatus.output.parsed);
                }

                if (valueToStore) {
                  if (isWorkspacesView) {
                    actions.updateWorkspaceField(item.id, columnName, valueToStore);
                  } else {
                    actions.updateCompanyField(item.id, columnName, valueToStore);
                  }
                  console.log(`Stored value for ${displayName}:`, valueToStore);
                }
              } else {
                console.warn(`Task completed but no output.parsed field for ${displayName}`);
              }

              // Store research basis if available
              if (taskStatus.sources && taskStatus.sources.length > 0) {
                actions.setTaskBasis(
                  item.id,
                  columnName,
                  taskResponse.researchId,
                  taskStatus.sources as any[]
                );
              }
            } else if (taskStatus.status === 'failed') {
              console.error(`Task failed for ${displayName}:`, taskStatus);
            }

            // Mark column as no longer enriching
            actions.setCompanyColumnEnriching(item.id, columnName, false);
          } catch (error) {
            console.error(`Failed to enrich ${displayName}:`, error);
            actions.setCompanyColumnEnriching(item.id, columnName, false);
            toast.error(`Failed to enrich ${displayName}`, {
              description: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        });

        // Wait for all enrichments to complete
        await Promise.all(enrichPromises);

        toast.success(`Enrichment completed for ${columnName}!`);
      } catch (error) {
        console.error('Failed to generate schema or start enrichment:', error);
        toast.error('Failed to start enrichment', {
          description: error instanceof Error ? error.message : 'Unknown error'
        });
        // Still add the column even if enrichment fails
        actions.addColumn(columnName, exaPrompt, 'exa');
      }
  };

  const handleParallelEnrichment = async (columnName: string, userPrompt: string) => {
    try {
      toast.info(`Generating output schema...`);

      // Step 1: Generate output schema using GPT-4o-mini (same as Exa)
      const outputSchema = await generateOutputSchema(userPrompt);
      console.log('Generated schema for Parallel:', outputSchema);

      // Add the column with the Parallel prompt
      actions.addColumn(columnName, userPrompt, 'parallel');

      // Determine which rows to enrich
      const selectedIndices = Object.keys(rowSelection).filter(key => rowSelection[key]).map(Number);
      const rowsToEnrich = selectedIndices.length > 0
        ? selectedIndices.map(index => currentData[index]).filter(Boolean)
        : currentData;

      console.log(`Enriching ${rowsToEnrich.length} rows (${selectedIndices.length > 0 ? 'selected' : 'all'})`);

      toast.info(`Starting Parallel enrichment for ${columnName} on ${rowsToEnrich.length} row${rowsToEnrich.length !== 1 ? 's' : ''}...`);

      // Process each selected/all company
      const enrichPromises = rowsToEnrich.map(async (item) => {
        const displayName = ('name' in item ? String(item.name) : String(item.id));

        try {
          // Mark column as enriching
          actions.setCompanyColumnEnriching(item.id, columnName, true);

          // Extract variables from prompt and build inputs object
          const inputs: Record<string, any> = {};

          // Check for all variables in the prompt and add them to inputs
          Object.keys(item).forEach((key) => {
            if (key !== 'id' && userPrompt.includes(`{${key}}`)) {
              inputs[key] = item[key];
            }
          });

          console.log(`Creating Parallel task for ${displayName} with inputs:`, inputs);

          // Create task using Task API directly with the generated schema
          const taskSpecPayload = {
            user_intent: userPrompt,
            output_schema: {
              json_schema: {
                additionalProperties: false,
                properties: outputSchema.properties,
                required: Object.keys(outputSchema.properties),
                type: "object",
              },
            },
          };

          const taskResponse = await createTask(taskSpecPayload, inputs);
          console.log(`Parallel task created for ${displayName}, run_id: ${taskResponse.run_id}`);

          // Poll for completion
          let taskStatus = await getTaskRun(taskResponse.run_id);
          console.log(`Task status for ${displayName}: ${taskStatus.status}`);

          while (
            taskStatus.status === "pending" ||
            taskStatus.status === "running" ||
            taskStatus.status === "queued"
          ) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            taskStatus = await getTaskRun(taskResponse.run_id);
            console.log(`Task status for ${displayName}: ${taskStatus.status}`);
          }

          if (taskStatus.status === "completed") {
            const result = await getTaskRunResult(taskResponse.run_id);
            console.log(`Parallel task completed for ${displayName}:`, result);

            // Update item with results
            if (result.output?.content) {
              const outputKeys = Object.keys(outputSchema.properties);

              if (outputKeys.length === 1) {
                // Single output field - unwrap value
                const firstKey = outputKeys[0];
                const value = result.output.content[firstKey];
                if (value !== undefined) {
                  if (isWorkspacesView) {
                    actions.updateWorkspaceField(item.id, columnName, String(value));
                  } else {
                    actions.updateCompanyField(item.id, columnName, String(value));
                  }
                }
              } else {
                // Multiple output fields - store as JSON
                const outputValues: Record<string, any> = {};
                outputKeys.forEach((key) => {
                  outputValues[key] = result.output.content[key];
                });
                if (isWorkspacesView) {
                  actions.updateWorkspaceField(item.id, columnName, JSON.stringify(outputValues));
                } else {
                  actions.updateCompanyField(item.id, columnName, JSON.stringify(outputValues));
                }
              }

              // Store task basis (confidence is already in the basis items)
              if (result.output.basis) {
                actions.setTaskBasis(item.id, columnName, taskResponse.run_id, result.output.basis);
              }
            }
          } else {
            console.error(`Parallel task failed for ${displayName}. Status: ${taskStatus.status}`);
          }

          // Mark column as no longer enriching
          actions.setCompanyColumnEnriching(item.id, columnName, false);

          // Decrement credits
          actions.decrementCredits(1);
        } catch (error) {
          console.error(`Failed to enrich ${displayName} with Parallel:`, error);
          actions.setCompanyColumnEnriching(item.id, columnName, false);
          toast.error(`Failed to enrich ${displayName}`, {
            description: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      });

      // Wait for all enrichments to complete
      await Promise.all(enrichPromises);

      toast.success(`Enrichment completed for ${columnName}!`);
    } catch (error) {
      console.error('Failed to generate schema or start enrichment:', error);
      toast.error('Failed to start enrichment', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
      // Still add the column even if enrichment fails
      actions.addColumn(columnName, userPrompt, 'parallel');
    }
  };

  const handleAnthropicEnrichment = async (columnName: string, userPrompt: string) => {
    try {
      toast.info(`Generating output schema...`);

      // Step 1: Generate output schema using GPT-4o-mini (same as other providers)
      const outputSchema = await generateOutputSchema(userPrompt);
      console.log('Generated schema for Anthropic:', outputSchema);

      // Add the column with the Anthropic prompt
      actions.addColumn(columnName, userPrompt, 'anthropic');

      // Determine which rows to enrich
      const selectedIndices = Object.keys(rowSelection).filter(key => rowSelection[key]).map(Number);
      const rowsToEnrich = selectedIndices.length > 0
        ? selectedIndices.map(index => currentData[index]).filter(Boolean)
        : currentData;

      console.log(`Enriching ${rowsToEnrich.length} rows (${selectedIndices.length > 0 ? 'selected' : 'all'})`);

      toast.info(`Starting Anthropic enrichment for ${columnName} on ${rowsToEnrich.length} row${rowsToEnrich.length !== 1 ? 's' : ''}...`);

      // Process each selected/all item
      const enrichPromises = rowsToEnrich.map(async (item) => {
        const displayName = ('name' in item ? String(item.name) : String(item.id));

        try {
          // Mark column as enriching
          actions.setCompanyColumnEnriching(item.id, columnName, true);

          // Build inputs object for template variables
          const inputs: Record<string, any> = {};
          Object.keys(item).forEach((key) => {
            if (key !== 'id') {
              inputs[key] = item[key];
            }
          });

          console.log(`Creating Anthropic enrichment for ${displayName} with inputs:`, inputs);

          // Call Anthropic enrichment API
          const result = await enrichWithAnthropic({
            prompt: userPrompt,
            outputSchema: {
              type: "object",
              properties: outputSchema.properties,
            },
            input: inputs,
          });

          console.log(`Anthropic enrichment completed for ${displayName}:`, result);

          // Update item with results
          if (result.output?.content) {
            const outputKeys = Object.keys(outputSchema.properties);

            if (outputKeys.length === 1) {
              // Single output field - unwrap value
              const firstKey = outputKeys[0];
              const value = result.output.content[firstKey];
              if (value !== undefined) {
                if (isWorkspacesView) {
                  actions.updateWorkspaceField(item.id, columnName, String(value));
                } else {
                  actions.updateCompanyField(item.id, columnName, String(value));
                }
              }
            } else {
              // Multiple output fields - store as JSON
              const outputValues: Record<string, any> = {};
              outputKeys.forEach((key) => {
                outputValues[key] = result.output.content[key];
              });
              if (isWorkspacesView) {
                actions.updateWorkspaceField(item.id, columnName, JSON.stringify(outputValues));
              } else {
                actions.updateCompanyField(item.id, columnName, JSON.stringify(outputValues));
              }
            }

            // Store task basis (confidence is already in the basis items)
            if (result.output.basis) {
              actions.setTaskBasis(item.id, columnName, `anthropic-${Date.now()}`, result.output.basis);
            }
          }

          // Mark column as no longer enriching
          actions.setCompanyColumnEnriching(item.id, columnName, false);

          // Decrement credits
          actions.decrementCredits(1);
        } catch (error) {
          console.error(`Failed to enrich ${displayName} with Anthropic:`, error);
          actions.setCompanyColumnEnriching(item.id, columnName, false);
          toast.error(`Failed to enrich ${displayName}`, {
            description: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      });

      // Wait for all enrichments to complete
      await Promise.all(enrichPromises);

      toast.success(`Enrichment completed for ${columnName}!`);
    } catch (error) {
      console.error('Failed to generate schema or start enrichment:', error);
      toast.error('Failed to start enrichment', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
      // Still add the column even if enrichment fails
      actions.addColumn(columnName, userPrompt, 'anthropic');
    }
  };

  const handleEnrich = (prompt: string) => {
    console.log("Enrich prompt:", prompt);
    actions.decrementCredits(1);
  };

  const handleAddCompany = (companyData: { name: string; domain: string }) => {
    actions.addCompany(companyData);
  };

  const handleRenameColumn = (oldName: string, newName: string) => {
    actions.renameColumn(oldName, newName);
    toast.success(`Column renamed to "${newName}"`);
  };

  const handleImportCSV = (data: { columns: string[]; rows: Workspace[] }) => {
    actions.importWorkspacesFromCSV(data);
    actions.setActiveView('workspaces');
  };

  const handleClearWorkspaces = () => {
    if (confirm(`Are you sure you want to clear all workspaces data? This cannot be undone.`)) {
      actions.clearWorkspaces();
      actions.setActiveView('companies');
      toast.success('Workspaces cleared');
    }
  };

  const getSortDescription = () => {
    const currentSort = sorting[0];
    const columnNameMap: Record<string, string> = {
      "select": "Selection",
      "id": "ID",
      "name": "Company name",
      "domain": "Domain",
    };

    // Add custom columns to the map
    snap.customColumns.forEach(col => {
      columnNameMap[col] = col;
    });

    const columnName = columnNameMap[currentSort.id] || currentSort.id;

    return `Sorted by ${columnName}`;
  };

  return (
    <TooltipProvider>
      <div className="w-screen h-screen flex bg-gray-50 overflow-hidden">
      {isSidebarVisible && <Sidebar onToggle={() => setIsSidebarVisible(false)} />}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="bg-white border-b border-[#eeeff1] px-[12px] py-0 h-[48px] flex items-center justify-between">
          {/* Left - Breadcrumbs */}
          <div className="flex items-center gap-[2px] h-[22px]">
            {!isSidebarVisible && (
              <button
                onClick={() => setIsSidebarVisible(true)}
                className="w-[28px] h-[28px] rounded-[8px] flex items-center justify-center hover:bg-gray-50 mr-2"
              >
                <PanelLeftOpen className="w-[16px] h-[16px] text-[#242629]" />
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center gap-1 px-1 py-0.5 rounded-md cursor-pointer hover:bg-gray-100">
                  <div className="w-[14px] h-[14px] bg-[#266df0] rounded-[3.5px] flex items-center justify-center">
                    <Building2 className="w-[7px] h-[9px] text-white" />
                  </div>
                  <div className="flex items-center gap-1 px-0.5">
                    <span className="font-medium text-[14px] text-[#242629] tracking-[-0.28px] leading-5">
                      {snap.activeView === 'companies' ? 'Companies' : 'Workspaces'}
                    </span>
                  </div>
                  <ChevronDown className="w-[14px] h-[14px] text-[#242629] opacity-60" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem
                  onClick={() => {
                    console.log('Switching to companies view');
                    actions.setActiveView('companies');
                  }}
                  className={snap.activeView === 'companies' ? 'bg-gray-100' : ''}
                >
                  {snap.activeView === 'companies' && <span className="mr-2">✓</span>}
                  Companies
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    console.log('Switching to workspaces view');
                    actions.setActiveView('workspaces');
                  }}
                  className={snap.activeView === 'workspaces' ? 'bg-gray-100' : ''}
                >
                  {snap.activeView === 'workspaces' && <span className="mr-2">✓</span>}
                  Workspaces
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsImportCSVModalOpen(true)}>
                  <Upload className="mr-2 h-4 w-4" />
                  Import Workspaces CSV
                </DropdownMenuItem>
                {snap.workspaces.length > 0 && (
                  <DropdownMenuItem
                    onClick={handleClearWorkspaces}
                    className="text-red-600 focus:text-red-600"
                  >
                    Clear Workspaces
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right - Actions */}
          <div className="flex items-center gap-[8px]">
            {/* Avatar Group */}
            <div className="flex items-center pr-[6px]">
              <div className="bg-white flex items-center justify-center w-[22px] h-[22px] rounded-full border-2 border-[#266df0] -mr-[6px] z-30">
                <div className="w-[20px] h-[20px] bg-[#266df0] rounded-full flex items-center justify-center">
                  <span className="text-[10px] font-medium text-white uppercase leading-5"></span>
                </div>
              </div>
              <div className="bg-white flex items-center justify-center w-[22px] h-[22px] rounded-full border-2 border-[#9b69ff] -mr-[6px] z-20">
                <div className="w-[20px] h-[20px] bg-[#f5a300] rounded-full flex items-center justify-center">
                  <span className="text-[10px] font-medium text-white uppercase leading-5"></span>
                </div>
              </div>
              <div className="bg-white flex items-center justify-center w-[22px] h-[22px] rounded-full border-2 border-[#f97514] -mr-[6px] z-10">
                <div className="w-[20px] h-[20px] rounded-full overflow-hidden">
                  <div className="w-full h-full bg-gray-300"></div>
                </div>
              </div>
              <div className="bg-[#f8f9fa] flex items-center justify-center w-[22px] h-[22px] rounded-full border border-[#e6e7ea]">
                <span className="text-[10px] font-medium text-[rgba(0,0,0,0.55)] leading-[14px] tracking-[-0.1px]">+1</span>
              </div>
            </div>

            {/* Divider */}
            <div className="w-0 h-[14px] border-l border-[#eeeff1]"></div>

            {/* Action Buttons */}
            <div className="flex items-center">
              <button className="w-[28px] h-[28px] rounded-[8px] flex items-center justify-center hover:bg-gray-50">
                <MessageCircle className="w-[14px] h-[14px] text-[#242629]" />
              </button>
              <button className="w-[28px] h-[28px] rounded-[8px] flex items-center justify-center hover:bg-gray-50">
                <HelpCircle className="w-[14px] h-[14px] text-[#242629]" />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-[28px] h-[28px] rounded-[8px] bg-[rgba(0,0,0,0.05)] flex items-center justify-center">
                    <MoreVertical className="w-[14px] h-[14px] text-[#242629]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsEnrichModalOpen(true)}>
                    Enrich
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="bg-white border-b border-[#eeeff1] px-3 py-0 h-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-white flex items-center gap-1 h-7 pl-0.5 pr-1.5 rounded-lg shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.04)] border border-black/7">
              <div className="flex items-center gap-1 px-1 h-6">
                <Building2 className="w-4 h-4 text-[#00d17e]" />
                <span className="font-medium text-sm text-[#242629] tracking-[-0.28px]">All companies</span>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-[#242629]" />
            </div>
            <div className="bg-white flex items-center gap-1 h-7 pl-0.5 pr-1.5 rounded-lg shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.04)] border border-black/7">
              <div className="flex items-center gap-1 px-1 h-6">
                <span className="font-medium text-sm text-[#242629] tracking-[-0.28px]">View settings</span>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-[#242629]" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="bg-white flex items-center gap-1 h-7 px-1.5 min-w-7 rounded-lg shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.04)] border border-black/7 hover:bg-gray-50">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <Settings className="w-3 h-3 text-[#242629]" />
              </div>
              <span className="font-medium text-sm text-[#242629] tracking-[-0.28px]">View settings</span>
            </button>
            <button
              onClick={() => setIsAddCompanyModalOpen(true)}
              className="bg-[#266df0] flex items-center gap-1 h-7 px-1.5 min-w-7 rounded-lg shadow-[0px_2px_4px_-2px_rgba(15,107,233,0.12),0px_3px_6px_-2px_rgba(15,107,233,0.08)] border border-black/10 hover:bg-blue-600"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <Plus className="w-2.5 h-2.5 text-white" />
              </div>
              <span className="font-medium text-sm text-white tracking-[-0.28px]">New company</span>
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white border-b border-[#eeeff1] px-3 py-2 h-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button className="bg-[#fbfbfb] flex items-center gap-1.5 h-7 px-2 rounded-lg border border-black/7 hover:bg-gray-50">
              <ArrowDownUp className="w-3.5 h-3.5 text-[#242629]" />
              <span className="font-medium text-sm text-[#242629] tracking-[-0.28px]">{getSortDescription()}</span>
            </button>
            <div className="h-4 w-px bg-[#eeeff1]"></div>
            <button className="bg-[#fbfbfb] flex items-center gap-1.5 h-7 px-2 rounded-lg border border-black/7 hover:bg-gray-50">
              <Filter className="w-3.5 h-3.5 text-[#242629]" />
              <span className="font-medium text-sm text-[#242629] tracking-[-0.28px]">Filter</span>
            </button>
          </div>
          {Object.keys(rowSelection).filter(key => rowSelection[key]).length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#242629]">
                {Object.keys(rowSelection).filter(key => rowSelection[key]).length} row{Object.keys(rowSelection).filter(key => rowSelection[key]).length !== 1 ? 's' : ''} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRowSelection({})}
                className="h-7"
              >
                Clear Selection
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 bg-white overflow-hidden">
          <DataTable
            columns={columns}
            data={currentData as any[]}
            sorting={sorting}
            onSortingChange={setSorting}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
          />
        </div>
      </div>

      <AddColumnModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onAddColumn={handleAddColumn}
      />
      <AddCompanyModal
        open={isAddCompanyModalOpen}
        onOpenChange={setIsAddCompanyModalOpen}
        onAddCompany={handleAddCompany}
      />
      <EnrichModal
        open={isEnrichModalOpen}
        onOpenChange={setIsEnrichModalOpen}
        onEnrich={handleEnrich}
      />
      <TaskBasisPanel
        open={basisPanelState?.open || false}
        onOpenChange={(open) =>
          setBasisPanelState((prev) => (prev ? { ...prev, open } : null))
        }
        taskBasis={
          basisPanelState
            ? snap.taskBasisData[basisPanelState.companyId]?.[
                basisPanelState.columnName
              ] || null
            : null
        }
        companyName={basisPanelState?.companyName || ""}
        columnName={basisPanelState?.columnName || ""}
      />
      <RenameColumnModal
        open={renameModalState.open}
        onOpenChange={(open) =>
          setRenameModalState((prev) => ({ ...prev, open }))
        }
        currentName={renameModalState.columnName}
        onRename={handleRenameColumn}
      />
      <ImportCSVModal
        open={isImportCSVModalOpen}
        onOpenChange={setIsImportCSVModalOpen}
        onImport={handleImportCSV}
      />
    </div>
    </TooltipProvider>
  );
}
